import { getEmojiByName } from "@/assets/emojis";
import CustomViewDialog from "@/components/customViews/CustomViewDialog";
import EmailView from "@/components/mailsList/EmailView";
import Spinner from "@/components/ui/spinner";
import CustomView from "@/domain/entities/customViews/CustomView";
import useCustomViewAwaitingMails from "@/hooks/customView/useCustomViewAwaitingMails";
import useGetView from "@/hooks/customView/useGetView";
import useGetMailsList from "@/hooks/mails/useGetMailsList";
import { Settings } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const ViewTitle = ({ customView }: { customView: CustomView }) => (
    <>
        <div className="flex min-w-0 items-center justify-start gap-3 text-left">
            <span className="shrink-0 text-2xl">
                {getEmojiByName(customView.icon)?.symbol}
            </span>
            <h2 className="text-main-title truncate text-3xl font-bold">
                {customView.name}
            </h2>
            <CustomViewDialog
                key={customView.id}
                trigger={
                    <div className="rounded-full bg-white p-1.5 text-text-caption transition-colors hover:bg-surface-activeFg">
                        <Settings className="h-5 w-5" />
                    </div>
                }
                viewId={customView.id}
            />
        </div>
        <hr className="my-2" />
    </>
);

const CustomViewEmails = () => {
    const location = useLocation();
    const customViewId = location.pathname.split("/").pop() as string;
    const navigate = useNavigate();
    const { data: customView } = useGetView(customViewId);

    if (!customViewId) {
        navigate("/404");
    }

    const {
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        flattenedMails,
        updateVersion,
    } = useGetMailsList(customViewId);

    const { showSkeleton, isConfiguring } = useCustomViewAwaitingMails(
        customViewId,
        flattenedMails.length,
        isFetching,
    );

    if (!customView) {
        return (
            <div className="flex h-full w-full flex-1 items-center justify-center font-display">
                <Spinner />
            </div>
        );
    }

    return (
        <EmailView
            key={customViewId}
            customView={customView}
            displayMode={customView.displayMode}
            hasNextPage={hasNextPage}
            isAwaitingMails={showSkeleton}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            isViewConfiguring={isConfiguring}
            listId={customViewId}
            loadNextPage={fetchNextPage}
            mails={flattenedMails}
            title={<ViewTitle customView={customView} />}
            updateVersion={updateVersion}
        />
    );
};

export default CustomViewEmails;
